import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const PendingAssignments = () => {
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [stats, setStats] = useState({});

  // Modal state for approval/denial
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionType, setActionType] = useState(""); // 'approve' or 'deny'
  const [responseNote, setResponseNote] = useState("");
  const [denialReason, setDenialReason] = useState("");

  useEffect(() => {
    fetchPendingAssignments();
    fetchUserStats();
  }, []);

  const fetchPendingAssignments = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "https://dailybackend-zvvt.onrender.com/api/daily-items/pending-assignments",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPendingAssignments(response.data);
    } catch (error) {
      console.error("Error fetching pending assignments:", error);
      toast.error("Failed to fetch pending assignments");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "https://dailybackend-zvvt.onrender.com/api/daily-items/user-stats",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching user stats:", error);
    }
  };

  const handleAction = async (item, action) => {
    setSelectedItem(item);
    setActionType(action);
    setResponseNote("");
    setDenialReason("");
    setShowModal(true);
  };

  const submitAction = async () => {
    if (!selectedItem) return;

    if (actionType === "deny" && !denialReason.trim()) {
      toast.error("Please provide a reason for denial");
      return;
    }

    setProcessing(selectedItem._id);

    try {
      const token = localStorage.getItem("token");
      const endpoint = `https://dailybackend-zvvt.onrender.com/api/daily-items/${selectedItem._id}/${actionType}`;

      const payload = {};
      if (actionType === "approve") {
        if (responseNote.trim()) payload.responseNote = responseNote.trim();
      } else {
        payload.denialReason = denialReason.trim();
        if (responseNote.trim()) payload.responseNote = responseNote.trim();
      }

      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(
        `Assignment ${
          actionType === "approve" ? "approved" : "denied"
        } successfully!`
      );

      // Refresh data
      fetchPendingAssignments();
      fetchUserStats();

      // Close modal
      setShowModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error(`Error ${actionType}ing assignment:`, error);
      toast.error(
        error.response?.data?.message || `Failed to ${actionType} assignment`
      );
    } finally {
      setProcessing(null);
    }
  };

  const markAsNotified = async (itemId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `https://dailybackend-zvvt.onrender.com/api/daily-items/${itemId}/mark-notified`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchPendingAssignments();
      fetchUserStats();
    } catch (error) {
      console.error("Error marking as notified:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFrequencyDisplay = (frequency, weeklyDays, monthlyDate) => {
    switch (frequency) {
      case "daily":
        return "Daily";
      case "weekly":
        return `Weekly (${weeklyDays?.join(", ") || "All days"})`;
      case "monthly":
        return `Monthly (Day ${monthlyDate || 1})`;
      default:
        return frequency;
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "60px" }}>
        <div style={{ fontSize: "48px", marginBottom: "20px" }}>⏳</div>
        <h3 style={{ color: "#666", marginBottom: "10px" }}>
          Loading Pending Assignments...
        </h3>
        <p style={{ color: "#999" }}>
          Please wait while we fetch your assignments
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
      {/* Enhanced Header with Stats */}
      <div
        className="card"
        style={{
          marginBottom: "30px",
          background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
          color: "white",
        }}
      >
        <div style={{ marginBottom: "25px" }}>
          <h2
            style={{
              fontSize: "28px",
              fontWeight: "bold",
              margin: "0 0 10px 0",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            📋 Pending Daily Item Assignments
          </h2>
          <p style={{ margin: 0, opacity: 0.9, fontSize: "16px" }}>
            Review and respond to business assignment requests
          </p>
        </div>

        {/* Enhanced Stats Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "20px",
          }}
        >
          <div
            style={{
              background: "rgba(245, 158, 11, 0.9)",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center",
              boxShadow: "0 8px 25px rgba(245, 158, 11, 0.3)",
              border: "2px solid rgba(255,255,255,0.2)",
            }}
          >
            <div
              style={{ fontSize: "14px", opacity: 0.9, marginBottom: "5px" }}
            >
              ⏳ Pending
            </div>
            <div style={{ fontSize: "28px", fontWeight: "bold" }}>
              {stats.pending || 0}
            </div>
          </div>
          <div
            style={{
              background: "rgba(34, 197, 94, 0.9)",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center",
              boxShadow: "0 8px 25px rgba(34, 197, 94, 0.3)",
            }}
          >
            <div
              style={{ fontSize: "14px", opacity: 0.9, marginBottom: "5px" }}
            >
              ✅ Approved
            </div>
            <div style={{ fontSize: "28px", fontWeight: "bold" }}>
              {stats.approved || 0}
            </div>
          </div>
          <div
            style={{
              background: "rgba(239, 68, 68, 0.9)",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center",
              boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
            }}
          >
            <div
              style={{ fontSize: "14px", opacity: 0.9, marginBottom: "5px" }}
            >
              ❌ Denied
            </div>
            <div style={{ fontSize: "28px", fontWeight: "bold" }}>
              {stats.denied || 0}
            </div>
          </div>
          <div
            style={{
              background: "rgba(255,255,255,0.15)",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <div
              style={{ fontSize: "14px", opacity: 0.9, marginBottom: "5px" }}
            >
              📊 Total
            </div>
            <div style={{ fontSize: "28px", fontWeight: "bold" }}>
              {stats.total || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Pending Assignments List */}
      {pendingAssignments.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "60px" }}>
          <div style={{ fontSize: "64px", marginBottom: "20px" }}>🎉</div>
          <h3 style={{ color: "#666", marginBottom: "10px", fontSize: "24px" }}>
            No pending assignments!
          </h3>
          <p style={{ color: "#999", fontSize: "16px" }}>
            You're all caught up with your daily item requests.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
          {pendingAssignments.map((item) => (
            <div
              key={item._id}
              className="card"
              style={{
                position: "relative",
                transition: "all 0.3s ease",
                border: "2px solid #f59e0b",
                borderLeft: "6px solid #f59e0b",
              }}
            >
              {/* Status Badge */}
              <div
                style={{
                  position: "absolute",
                  top: "20px",
                  right: "20px",
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "white",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  boxShadow: "0 4px 15px rgba(245, 158, 11, 0.3)",
                }}
              >
                ⏳ Pending Approval
              </div>

              <div style={{ paddingRight: "150px" }}>
                <h3
                  style={{
                    fontSize: "22px",
                    fontWeight: "bold",
                    color: "#333",
                    marginBottom: "15px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  📦 {item.title}
                </h3>
                <p
                  style={{
                    color: "#666",
                    marginBottom: "25px",
                    fontSize: "16px",
                    lineHeight: "1.6",
                  }}
                >
                  {item.description}
                </p>

                {/* Enhanced Information Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "15px",
                    marginBottom: "25px",
                  }}
                >
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                      color: "white",
                      padding: "15px",
                      borderRadius: "12px",
                      boxShadow: "0 4px 15px rgba(59, 130, 246, 0.3)",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      🏢 Business Information
                    </div>
                    <div style={{ fontSize: "15px", marginBottom: "4px" }}>
                      {item.createdBy?.businessName || item.createdBy?.username}
                    </div>
                    <div style={{ fontSize: "13px", opacity: 0.9 }}>
                      📧 {item.createdBy?.email}
                    </div>
                  </div>

                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                      color: "white",
                      padding: "15px",
                      borderRadius: "12px",
                      boxShadow: "0 4px 15px rgba(139, 92, 246, 0.3)",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      📂 Item Details
                    </div>
                    <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                      Category: {item.category}
                    </div>
                    <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                      Quantity: {item.quantity}
                    </div>
                    <div style={{ fontSize: "14px" }}>
                      Price: ${item.price || 0}
                    </div>
                  </div>

                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "white",
                      padding: "15px",
                      borderRadius: "12px",
                      boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      🔄 Schedule Information
                    </div>
                    <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                      Frequency:{" "}
                      {getFrequencyDisplay(
                        item.frequency,
                        item.weeklyDays,
                        item.monthlyDate
                      )}
                    </div>
                    <div style={{ fontSize: "14px" }}>
                      Delivery Time: {item.deliveryTime}
                    </div>
                  </div>

                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "white",
                      padding: "15px",
                      borderRadius: "12px",
                      boxShadow: "0 4px 15px rgba(245, 158, 11, 0.3)",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      📅 Timeline
                    </div>
                    <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                      Start: {formatDate(item.startDate)}
                    </div>
                    {!item.isIndefinite && item.endDate && (
                      <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                        End: {formatDate(item.endDate)}
                      </div>
                    )}
                    <div style={{ fontSize: "14px" }}>
                      Requested: {formatDate(item.assignmentRequestDate)}
                    </div>
                  </div>
                </div>

                {/* Enhanced Action Buttons */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr auto",
                    gap: "15px",
                    paddingTop: "25px",
                    borderTop: "2px solid #f1f5f9",
                  }}
                >
                  <button
                    onClick={() => handleAction(item, "approve")}
                    disabled={processing === item._id}
                    className="btn"
                    style={{
                      background:
                        processing === item._id
                          ? "#ccc"
                          : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "white",
                      border: "none",
                      padding: "15px 25px",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderRadius: "10px",
                      boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                      cursor:
                        processing === item._id ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    {processing === item._id
                      ? "⏳ Processing..."
                      : "✅ Approve"}
                  </button>

                  <button
                    onClick={() => handleAction(item, "deny")}
                    disabled={processing === item._id}
                    className="btn"
                    style={{
                      background:
                        processing === item._id
                          ? "#ccc"
                          : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      color: "white",
                      border: "none",
                      padding: "15px 25px",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderRadius: "10px",
                      boxShadow: "0 4px 15px rgba(239, 68, 68, 0.3)",
                      cursor:
                        processing === item._id ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    {processing === item._id ? "⏳ Processing..." : "❌ Deny"}
                  </button>

                  <button
                    onClick={() => markAsNotified(item._id)}
                    className="btn"
                    style={{
                      background:
                        "linear-gradient(135deg, #64748b 0%, #475569 100%)",
                      color: "white",
                      border: "none",
                      padding: "15px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      borderRadius: "10px",
                      boxShadow: "0 4px 15px rgba(100, 116, 139, 0.3)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    👁️ Mark Read
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Modal */}
      {showModal && selectedItem && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              maxWidth: "500px",
              width: "100%",
              margin: "16px",
            }}
          >
            <div style={{ padding: "30px" }}>
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  marginBottom: "20px",
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {actionType === "approve"
                  ? "✅ Approve Assignment"
                  : "❌ Deny Assignment"}
              </h3>

              <div
                style={{
                  marginBottom: "25px",
                  padding: "20px",
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                  borderRadius: "8px",
                  border: "2px solid #e2e8f0",
                }}
              >
                <p
                  style={{
                    color: "#475569",
                    marginBottom: "8px",
                    fontSize: "16px",
                  }}
                >
                  <strong>📦 Item:</strong> {selectedItem.title}
                </p>
                <p style={{ color: "#475569", fontSize: "16px" }}>
                  <strong>🏢 From:</strong>{" "}
                  {selectedItem.createdBy?.businessName}
                </p>
              </div>

              {actionType === "deny" && (
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "10px",
                    }}
                  >
                    Reason for denial *
                  </label>
                  <textarea
                    value={denialReason}
                    onChange={(e) => setDenialReason(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #d1d5db",
                      borderRadius: "8px",
                      resize: "none",
                      fontSize: "14px",
                      fontFamily: "inherit",
                    }}
                    rows="3"
                    placeholder="Please explain why you're denying this assignment..."
                    required
                  />
                </div>
              )}

              <div style={{ marginBottom: "30px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "10px",
                  }}
                >
                  Additional note (optional)
                </label>
                <textarea
                  value={responseNote}
                  onChange={(e) => setResponseNote(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #d1d5db",
                    borderRadius: "8px",
                    resize: "none",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                  rows="2"
                  placeholder="Add any additional comments..."
                />
              </div>

              <div style={{ display: "flex", gap: "15px" }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: "2px solid #d1d5db",
                    color: "#374151",
                    borderRadius: "8px",
                    background: "white",
                    cursor: "pointer",
                    fontSize: "16px",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitAction}
                  disabled={actionType === "deny" && !denialReason.trim()}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    borderRadius: "8px",
                    color: "white",
                    border: "none",
                    cursor:
                      actionType === "deny" && !denialReason.trim()
                        ? "not-allowed"
                        : "pointer",
                    fontSize: "16px",
                    fontWeight: "600",
                    background:
                      actionType === "approve"
                        ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    opacity:
                      actionType === "deny" && !denialReason.trim() ? 0.5 : 1,
                    transition: "all 0.3s ease",
                    boxShadow:
                      actionType === "approve"
                        ? "0 4px 15px rgba(16, 185, 129, 0.3)"
                        : "0 4px 15px rgba(239, 68, 68, 0.3)",
                  }}
                >
                  {actionType === "approve" ? "✅ Approve" : "❌ Deny"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingAssignments;
